import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTheme } from './contexts/ThemeContext';
import { UserRole } from './services/users';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
// REGISTRO PÚBLICO DESABILITADO - import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Events from './pages/Events';
import Assets from './pages/Assets';

import Companies from './pages/Companies';
import Users from './pages/Users';
import CollectorsNew from './pages/CollectorsNew';
import Reports from './pages/Reports';
import DomainInvestigation from './pages/DomainInvestigation';
import LeakedCredentials from './pages/LeakedCredentials';
import Tickets from './pages/Tickets';
import Takedowns from './pages/Takedowns';
import Profile from './pages/Profile';
import Registration from './pages/Registration';

import RiskDashboard from './components/RiskDashboard';
import APTFeed from './pages/APTFeed';
import Integrations from './pages/Integrations';
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/UI/LoadingSpinner';
import ProtectedRoute from './components/ProtectedRoute';

const App: React.FC = () => {
  const { user, loading } = useAuth();
  const { theme, setTheme } = useTheme();

  // Apply theme class to document
  React.useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Default to dark for operational roles on first visit (no saved preference)
  React.useEffect(() => {
    if (user && !localStorage.getItem('theme')) {
      if (user.role === UserRole.ANALYST || user.role === UserRole.ADMINISTRATOR) {
        setTheme('dark');
      }
    }
  }, [user?.id]);

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <LoadingSpinner size='lg' />
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path='/login' element={<Login />} />
        <Route path='/forgot-password' element={<ForgotPassword />} />
        <Route path='/reset-password' element={<ResetPassword />} />
        {/* REGISTRO PÚBLICO DESABILITADO - <Route path="/register" element={<Register />} /> */}
        <Route path='*' element={<Navigate to='/login' replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path='/' element={<Navigate to='/dashboard' replace />} />
        <Route path='/dashboard' element={<Dashboard />} />
        <Route
          path='/events/*'
          element={
            <ProtectedRoute requiredRoles={[UserRole.ANALYST, UserRole.ADMINISTRATOR]}>
              <Events />
            </ProtectedRoute>
          }
        />
        <Route path='/assets' element={<Assets />} />

        <Route
          path='/companies'
          element={
            <ProtectedRoute requiredRoles={[UserRole.ANALYST, UserRole.ADMINISTRATOR]}>
              <Companies />
            </ProtectedRoute>
          }
        />
        <Route
          path='/users'
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMINISTRATOR]}>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path='/collectors'
          element={
            <ProtectedRoute requiredRoles={[UserRole.ANALYST, UserRole.ADMINISTRATOR]}>
              <CollectorsNew />
            </ProtectedRoute>
          }
        />
        <Route path='/reports' element={<Reports />} />
        <Route path="/registration"
          element={
            <ProtectedRoute requiredRoles={[UserRole.CLIENT]}>
                <Registration />
            </ProtectedRoute>
          }
        />
        <Route
          path='/domain-investigation'
          element={
            <ProtectedRoute requiredRoles={[UserRole.ADMINISTRATOR]}>
              <DomainInvestigation />
            </ProtectedRoute>
          }
        />
        <Route path='/leaked-credentials' element={<LeakedCredentials />} />

        <Route path='/dynamic-risk' element={<RiskDashboard />} />
        <Route path='/apt-feed' element={<APTFeed />} />
        <Route path='/tickets' element={<Tickets />} />
        <Route path='/takedowns' element={<Takedowns />} />
        <Route path='/profile' element={<Profile />} />
        <Route path='/integrations' element={<Integrations />} />
        <Route path='*' element={<Navigate to='/dashboard' replace />} />
      </Routes>
    </Layout>
  );
};

export default App;
